<template>
  <div class="flex flex-col w-full">
    <div
      v-if="offeredSymbol.description"
      class="items-start bg-neutral-primary text-neutral-secondary-press px-4 mb-3"
    >
      <NText variant="caption1" class="block">
        {{ offeredSymbol.description }}
      </NText>

      <a
        v-if="ipoConfig.link"
        :href="ipoConfig.link"
        target="_blank"
        class="inline-flex mt-3"
      >
        <NButton
          color="gray"
          variant="flat"
          size="mini"
          class="flex justify-center items-center"
        >
          <NegarLinkIcon size="16" class="me-1" />

          <NText variant="caption1">
            {{ t('ipo.moreInformation') }}
          </NText>
        </NButton>
      </a>
    </div>

    <div class="bg-neutral-secondary px-4 pt-3 rounded-md">
      <VeeForm
        v-slot="{ errors, handleSubmit, meta }"
        as=""
        :validate-on-mount="validateOnMount"
      >
        <form
          autocomplete="off"
          class="w-full"
          @submit="handleSubmit($event, onSubmit)"
        >
          <VeeField
            v-slot="{ field }"
            v-model="formModel.quantity"
            name="quantity"
            :rules="{
              required: true,
              between: [
                Number(offeredSymbol.minimumOrderQuantity),
                Number(offeredSymbol.maximumOrderQuantity),
                t('modules.order.order_count'),
              ],
            }"
          >
            <NInput
              v-mask="{
                alias: 'positiveNumber',
              }"
              :model-value="formModel.quantity"
              :disabled="isEqualQuantities"
              input-class="text-center font-bold !text-c1"
              v-bind="field"
              :error="errors.quantity"
              :required-sign="false"
              :show-error="true"
              type="tel"
              dir="ltr"
              required
              @focus="$event.target.select()"
              @update:model-value="changeQuantity($event)"
            >
              <template #prefix>
                <NText
                  variant="caption1"
                  weight="medium"
                  class="text-neutral-secondary-press"
                >
                  {{ t('misc.quantity') }}
                </NText>
              </template>
              <template #postfix />
            </NInput>
          </VeeField>

          <div class="flex items-center">
            <NButton
              v-if="!isEqualQuantities"
              size="mini"
              class="mb-3"
              :class="{
                '!bg-neutral-tertiary !text-neutral-secondary':
                  !isActiveShortcut.quantity.max,
              }"
              type="button"
              @click="
                changeQuantity(offeredSymbol.maximumOrderQuantity, {
                  min: false,
                  max: true,
                })
              "
            >
              <NumberFormatter
                #="{ number }"
                :value="offeredSymbol.maximumOrderQuantity"
              >
                <NText variant="overline" weight="medium">
                  {{ t('payment.maximum') }}: {{ number }}
                </NText>
              </NumberFormatter>
            </NButton>

            <NButton
              v-if="isShowMinimumQuantityButton"
              size="mini"
              class="mb-3 ms-1"
              :class="{
                '!bg-neutral-tertiary !text-neutral-secondary':
                  !isActiveShortcut.quantity.min,
              }"
              type="button"
              @click="
                changeQuantity(offeredSymbol.minimumOrderQuantity, {
                  min: true,
                  max: false,
                })
              "
            >
              <NumberFormatter
                #="{ number }"
                :value="offeredSymbol.minimumOrderQuantity"
              >
                <NText variant="overline" weight="medium">
                  {{ t('payment.minimum') }}: {{ number }}
                </NText>
              </NumberFormatter>
            </NButton>
          </div>

          <template v-if="orderType !== IPO_ORDER_TYPE.preOrderNoPrice">
            <VeeField
              v-slot="{ field }"
              v-model="formModel.price"
              name="price"
              :rules="{
                required: true,
                between: [
                  Number(offeredSymbol.minimumOrderPrice),
                  Number(offeredSymbol.maximumOrderPrice),
                  t('modules.order.order_price'),
                ],
              }"
            >
              <NInput
                v-mask="{
                  alias: 'positiveNumber',
                }"
                :model-value="formModel.price"
                :disabled="isEqualPrices"
                input-class="text-center font-bold !text-c1"
                v-bind="field"
                :error="errors.price"
                :required-sign="false"
                :show-error="true"
                type="tel"
                dir="ltr"
                required
                @focus="$event.target.select()"
                @update:model-value="changePrice($event)"
              >
                <template #prefix>
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-secondary-press"
                  >
                    {{ t('symbol.price') }}
                  </NText>
                </template>
                <template #postfix>
                  <NText
                    variant="overline"
                    weight="medium"
                    class="text-neutral-secondary"
                  >
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>
            </VeeField>

            <div class="flex items-center">
              <NButton
                v-if="!isEqualPrices"
                size="mini"
                class="mb-3"
                :class="{
                  '!bg-neutral-tertiary !text-neutral-secondary':
                    !isActiveShortcut.price.max,
                }"
                type="button"
                @click="
                  changePrice(offeredSymbol.maximumOrderPrice, {
                    min: false,
                    max: true,
                  })
                "
              >
                <NumberFormatter
                  #="{ number }"
                  :value="offeredSymbol.maximumOrderPrice"
                >
                  <NText variant="overline" weight="medium">
                    {{ t('payment.maximum') }}: {{ number }}
                  </NText>
                </NumberFormatter>
              </NButton>

              <NButton
                v-if="!isEqualPrices"
                size="mini"
                class="mb-3 ms-1"
                :class="{
                  '!bg-neutral-tertiary !text-neutral-secondary':
                    !isActiveShortcut.price.min,
                }"
                type="button"
                @click="
                  changePrice(offeredSymbol.minimumOrderPrice, {
                    min: true,
                    max: false,
                  })
                "
              >
                <NumberFormatter
                  #="{ number }"
                  :value="offeredSymbol.minimumOrderPrice"
                >
                  <NText variant="overline" weight="medium">
                    {{ t('payment.minimum') }}: {{ number }}
                  </NText>
                </NumberFormatter>
              </NButton>
            </div>

            <VeeField
              v-slot="{ field }"
              v-model="formModel.totalValue"
              name="totalValue"
              :rules="{
                required: true,
              }"
            >
              <NInput
                v-mask="{
                  alias: 'positiveNumber',
                }"
                :model-value="formModel.totalValue"
                :disabled="isEqualQuantities && isEqualPrices"
                v-bind="field"
                input-class="text-center font-bold !text-c1"
                :error="errors.totalValue"
                :required-sign="false"
                :show-error="true"
                type="tel"
                dir="ltr"
                required
                @focus="$event.target.select()"
                @update:model-value="changeTotalValue($event)"
              >
                <template #prefix>
                  <NText
                    variant="caption1"
                    weight="medium"
                    class="text-neutral-secondary-press"
                  >
                    {{ t('misc.value') }}
                  </NText>
                </template>
                <template #postfix>
                  <NText
                    variant="overline"
                    weight="medium"
                    class="text-neutral-secondary"
                  >
                    {{ t('misc.rial') }}
                  </NText>
                </template>
              </NInput>
            </VeeField>

            <div class="flex justify-between items-center px-4">
              <div class="inline-flex items-center text-neutral-secondary">
                <NegarMoneyLightIcon size="16" class="me-1" />
                <NText variant="overline" weight="medium">
                  {{ t('common.buy_power') }}:
                </NText>
              </div>

              <div class="text-neutral-secondary">
                <NumberFormatter
                  #="{ number }"
                  :value="cashData.buyingPower ?? 0"
                >
                  <NText
                    variant="overline"
                    weight="medium"
                    @click="changeTotalValue(cashData.buyingPower)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
                <NText
                  variant="overline"
                  weight="medium"
                  class="ms-0.5 align-middle"
                >
                  {{ t('misc.rial') }}
                </NText>
              </div>
            </div>
          </template>

          <template v-else>
            <NBox>
              <VeeField v-slot="{ field }" name="noPriceChecked">
                <label
                  class="flex items-center border-b border-neutral-secondary py-3 px-2"
                >
                  <NCheckbox v-model="formModel.noPriceChecked" v-bind="field">
                    <NText weight="medium" variant="caption1">
                      {{ t('ipo.preOrderWithNoPriceApproved') }}
                    </NText>
                  </NCheckbox>
                </label>
              </VeeField>
            </NBox>
          </template>

          <template v-if="type === FORM_TYPES.edit">
            <div class="flex justify-center my-5">
              <NButton
                variant="fill"
                class="w-2/3 font-bold"
                type="submit"
                color="green"
                :loading="loading"
                :disabled="loading || !meta.valid"
              >
                {{ t('ipo.editBuy') }}
              </NButton>

              <NButton
                variant="outline"
                color="gray"
                class="w-1/3 font-bold ms-3"
                type="button"
                @click="remove()"
              >
                {{ t('common.cancel') }}
              </NButton>
            </div>
          </template>

          <template v-else>
            <NButton
              variant="fill"
              class="w-full font-bold my-5"
              type="submit"
              :loading="loading"
              :disabled="
                loading ||
                !meta.valid ||
                (orderType === IPO_ORDER_TYPE.preOrderNoPrice
                  ? !formModel.noPriceChecked
                  : false)
              "
            >
              {{
                orderType === IPO_ORDER_TYPE.preOrder ||
                orderType === IPO_ORDER_TYPE.preOrderNoPrice
                  ? t('ipo.submitBuyRequest')
                  : t('ipo.sendBuy')
              }}
            </NButton>
          </template>
        </form>
      </VeeForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarMoneyLightIcon, NegarLinkIcon } from '@negar/icons';
import { useDialog } from '@negar/core';
import { useLiveCashQuery } from '@core/cash';
import {
  useSymbolDeductions,
  calcSymbolBuyValue,
  calcSymbolQuantityBuy,
} from '@core/symbol';
import {
  FORM_TYPES,
  IPO_CONFIG,
  IPO_CONFIG_DEFAULT,
  IPO_ORDER_TYPE,
} from '@core/ipo';
import { OrderItem, InitialPublicOfferItem } from '@libs/api-reference';

import NumberFormatter from '@/components/Number.vue';

import vMask from '@/directives/mask';
import { MANUAL_QUANTITY_INSTRUMENT_ISIN } from '../constants';

type OfferFormType = {
  validateOnMount?: boolean;
  offeredSymbol: InitialPublicOfferItem;
  order?: OrderItem;
  orderType?: string;
  loading?: boolean;
  type: string;
};

const props = withDefaults(defineProps<OfferFormType>(), {
  validateOnMount: false,
  order: undefined,
  orderType: '',
  loading: false,
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const { remove } = useDialog();

const { cashData } = useLiveCashQuery();
const { findWageConfigBySymbol } = useSymbolDeductions();
const wage = findWageConfigBySymbol(props.offeredSymbol);

const isActiveShortcut = ref({
  quantity: { min: false, max: props.type === FORM_TYPES.edit ? false : true },
  price: { min: false, max: false },
});

const formModel = reactive({
  quantity: props.order?.quantity || props.offeredSymbol.maximumOrderQuantity,
  price: props.order?.price || props.offeredSymbol.maximumOrderPrice,
  totalValue: 0,
  noPriceChecked: null,
});

const realValue = computed(() => {
  return calcSymbolBuyValue(formModel, wage);
});

const quantity = computed(() => {
  const roundUp = formModel.totalValue >= cashData.value.buyingPower;

  return calcSymbolQuantityBuy(formModel, wage, roundUp);
});

const changeTotalValue = (value) => {
  formModel.totalValue = value;
  if (formModel.price) {
    formModel.quantity = quantity.value;
  }
};

const changePrice = (value, shortcutPrices = { min: false, max: false }) => {
  formModel.price = value;
  changeActivePriceShortcut(shortcutPrices.min, shortcutPrices.max);
  if (formModel.quantity) {
    formModel.totalValue = realValue.value;
  } else if (formModel.totalValue) {
    formModel.quantity = quantity.value;
  }
};

const changeQuantity = (
  value,
  shortcutQuantity = { min: false, max: false }
) => {
  formModel.quantity = value;
  changeActiveQuantityShortcut(shortcutQuantity.min, shortcutQuantity.max);
  if (formModel.price) {
    formModel.totalValue = realValue.value;
  }
};

const changeActivePriceShortcut = (min, max) => {
  isActiveShortcut.value.price.min = min;
  isActiveShortcut.value.price.max = max;
};

const changeActiveQuantityShortcut = (min, max) => {
  isActiveShortcut.value.quantity.min = min;
  isActiveShortcut.value.quantity.max = max;
};

const onSubmit = (model, { resetForm }) => {
  const resetFormModel = () => {
    resetForm({
      values: {
        quantity: isEqualQuantities.value ? formModel.quantity : null,
        price: isEqualPrices.value ? formModel.price : null,
        totalValue:
          isEqualQuantities.value && isEqualPrices.value
            ? formModel.totalValue
            : null,
      },
    });

    isActiveShortcut.value = {
      quantity: { min: false, max: false },
      price: { min: false, max: false },
    };
  };

  emit(
    'submit',
    {
      ...formModel,
      orderId: props.order?.orderId ? Number(props.order.orderId) : null,
      instrumentIsin: props.offeredSymbol.instrumentIsin,
    },
    resetFormModel
  );
};

const isEqualPrices = computed(() => {
  return (
    props.offeredSymbol.minimumOrderPrice ===
    props.offeredSymbol.maximumOrderPrice
  );
});

const isEqualQuantities = computed(() => {
  return (
    props.offeredSymbol.minimumOrderQuantity ===
    props.offeredSymbol.maximumOrderQuantity
  );
});

const isShowMinimumQuantityButton = computed(() => {
  return (
    !isEqualQuantities.value && props.offeredSymbol.minimumOrderQuantity > 1
  );
});

const ipoConfig = computed(() => {
  return (
    IPO_CONFIG.find(
      (item) => item.isin === props.offeredSymbol.instrumentIsin
    ) || IPO_CONFIG_DEFAULT
  );
});

watch(
  () => props.offeredSymbol,
  () => {
    if (isEqualPrices.value) {
      formModel.price = props.offeredSymbol.maximumOrderPrice;
    }
    if (isEqualQuantities.value) {
      formModel.quantity = props.offeredSymbol.maximumOrderQuantity;
    }

    // TODO: Remove this temporary exception once manual quantity is supported generically.
    // For this specific instrument, the quantity must be entered manually.
    const requiresManualQuantity =
      props.offeredSymbol.instrumentIsin === MANUAL_QUANTITY_INSTRUMENT_ISIN;

    if (requiresManualQuantity) {
      formModel.quantity = null as any;
    }
  },
  { immediate: true }
);

onMounted(() => {
  formModel.totalValue = realValue.value;
});
</script>
